import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import CommonTable, {
  Action,
  TableColumn,
} from '@/components/common/CommonTable';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import Layout from '@/components/Layout/Layout';
import RefundStatus from '@/components/order/RefundStatus';
import Card from '@/components/UI/Card';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { PageInfo } from '@/interfaces/brandsResponse.interface';
import {
  IRefund,
  IRefundResponse,
} from '@/interfaces/refundResponse.interface';
import { formatDateTime, GetFilterUrl, toFixedThree } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface StateProp {
  data: IRefund[];
  pageInfo: PageInfo;
  isLoading: boolean;
}

const RefundReturn: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<StateProp>({
    data: [],
    pageInfo: {
      total: 1,
      totalPages: 1,
      limit: 10,
      page: 1,
    },
    isLoading: false,
  });

  const handleChangeState = (newState: Partial<typeof state>) => {
    setState((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(state.data);

  const refundColumns: TableColumn<IRefund>[] = [
    {
      key: 'customerName',
      header: 'Name',
      render: (refund) => {
        return (
          <span
            className={
              refund?.order?.personalInfo.isEmployee ? 'text-blue-500' : ''
            }
          >
            {refund?.order?.personalInfo?.firstName}
          </span>
        );
      },
    },
    {
      key: 'orderId',
      header: 'Order ID',
      render: (refund) => {
        return (
          <span
            className={
              refund?.order?.personalInfo?.isEmployee ? 'text-blue-500' : ''
            }
          >
            {refund?.order?.orderId}
          </span>
        );
      },
    },
    {
      key: 'refundId',
      header: 'Refund ID',
      render: (refund) => {
        return (
          <span
            className={
              refund?.order?.personalInfo?.isEmployee ? 'text-blue-500' : ''
            }
          >
            {refund?.refundId}
          </span>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Date / Time',
      render: (refund) => {
        return (
          <span
            className={
              refund?.order?.personalInfo?.isEmployee ? 'text-blue-500' : ''
            }
          >
            {formatDateTime(refund.createdAt, 'YYMMMDDHH')}
          </span>
        );
      },
    },
    {
      key: 'total',
      header: 'Total Amount BD',
      render: (refund) => {
        return (
          <span
            className={
              refund?.order?.personalInfo?.isEmployee ? 'text-blue-500' : ''
            }
          >
            {toFixedThree(refund.amount)}
          </span>
        );
      },
    },
    {
      key: 'refundStatus',
      header: 'Refund Status',
      render: (refund) => {
        return (
          <div
            className={
              refund?.order?.personalInfo?.isEmployee ? 'text-blue-500' : ''
            }
          >
            <RefundStatus
              refundId={refund.refundId}
              status={refund.status}
              onSucess={() => fetchRefunds()}
            />
          </div>
        );
      },
    },
    {
      key: 'items',
      header: 'Items',
      render: (refund) => {
        return (
          <span
            className={
              refund?.order?.personalInfo?.isEmployee ? 'text-blue-500' : ''
            }
          >
            {refund.productIds?.length ?? 0}
          </span>
        );
      },
    },
  ];

  const actions: Action<IRefund>[] = [
    {
      text: 'View',
      onClick: (refund) =>
        navigate(`${RoutesLink.refundDetail}/${refund.refundId}`),
    },
  ];

  useEffect(() => {
    fetchRefunds();
  }, [state.pageInfo.page, state.pageInfo.limit]);

  const fetchRefunds = async () => {
    try {
      const filterQuery = {
        page: state.pageInfo.page,
        limit: state.pageInfo.limit,
      };
      const query = GetFilterUrl(filterQuery);
      setState((prev) => ({
        ...prev,
        isLoading: true,
      }));
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.refund}?${query ? query : ''}`;
      const res = await callApi<IRefundResponse>(PATH, { method: 'GET' });
      setState((prev) => ({
        ...prev,
        data: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...state.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...state.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  return (
    <Layout>
      <PageHeading text="Refund & Return" />
      <Card>
        <CommonTable
          columns={refundColumns}
          data={state.data}
          showCheckbox={false}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Refund/Return found"
          loading={state.isLoading}
          actions={actions}
        />
        <Pagination
          paginationData={state.pageInfo}
          onPageChange={(page) => handleChangePage(page)}
          onPageSizeChange={handleChangePageSize}
          isLoading={state.isLoading}
        />
      </Card>
    </Layout>
  );
};

export default RefundReturn;
